import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import React from 'react';
import CommonUtil from 'utils/CommonUtil';

/**
 * Deep compares all properties in `propertyNames` on `a` and `b`, and
 * returns true if they are all the same.
 */
function deepCompareReactProperties(description, propertyNames, a, b) {
  if (!isEmpty(propertyNames)) {
    return propertyNames.every((prop) => {
      const source = get(a, prop);
      const comparand = get(b, prop);

      let areObjectsEquivalent;
      if (Number.isNaN(source) && Number.isNaN(comparand)) {
        console.warn(
          `React rendering danger detected: the ${description} ${prop} ` +
            `source and comparand is NaN, NaN === NaN is always false, so we are skipping this property`,
        );
        areObjectsEquivalent = true;
      } else if (Number.isNaN(source) || Number.isNaN(comparand)) {
        console.warn(`React rendering danger detected: the ${description} ${prop} is NaN`);
        areObjectsEquivalent = false;
      } else if (React.isValidElement(source) || React.isValidElement(comparand)) {
        // Deep-comparing two React elements is a bad idea - they're basically
        // objects that have already been rendered.  We should just render them
        // and let React decide what to do with them.  Also, in React 16, this
        // basically hangs Chrome for a really long time.
        console.warn(
          `Cowardly refusing to deeep compare react elements from ${description} ${prop}`,
        );
        areObjectsEquivalent = source === comparand;
      } else {
        areObjectsEquivalent = isEqual(source, comparand);
      }

      return areObjectsEquivalent;
    });
  }

  return true;
}

class ComponentUtil {
  /**
   * Determines whether a node's (sub-)props have changed
   * @param  {Object} source      The old React node to compare against
   * @param  {Object} comparand   The new React node to compare against
   * @return {Bool}               Returns true if the node's (sub-)props have changed
   */
  static nodeHasNewProps({ source, comparand }) {
    if (!!source !== !!comparand) {
      return true;
    } else if (!source) {
      return false;
    }

    const sourceKids = get(source, 'props.children');
    const comparandKids = get(comparand, 'props.children');
    if (!!sourceKids !== !!comparandKids) {
      return true;
    }

    const sourceEmpty = isEmpty(sourceKids);
    const comparandEmpty = isEmpty(comparandKids);
    if (
      sourceEmpty !== comparandEmpty ||
      (sourceKids && sourceKids.length !== comparandKids.length)
    ) {
      return true;
    } else if (!sourceEmpty) {
      // Both `children` are NOT empty
      for (let index = 0; index < sourceKids.length; index++) {
        const sourceKid = sourceKids[index];
        const comparandKid = comparandKids[index];
        const kidHasNewProps = this.nodeHasNewProps({ source: sourceKid, comparand: comparandKid });
        if (kidHasNewProps) {
          return true;
        }
      }
    }

    const sourceProps = Object.keys(source.props);
    const comparandProps = Object.keys(comparand.props);
    if (!!sourceProps !== !!comparandProps) {
      return true;
    } else if (isEmpty(sourceProps)) {
      return false;
    }

    for (let index = 0; index < sourceProps.length; index++) {
      const propName = sourceProps[index];
      const sourceProp = source.props[propName];
      const comparandProp = comparand.props[propName];
      if (propName !== 'children' && !isEqual(sourceProp, comparandProp)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Determines whether a component should update
   * @param  {Object} context           The `this` context of the component
   * @param  {Object} nextProps         The incoming props for the component
   * @param  {Object} nextState         The incoming state for the component
   * @param  {Array} deepCompareProps   Array of props to do a deep comp on
   * @param  {Array} deepCompareStates  Array of states to do a deep comp on
   * @return {Bool}                     Returns true if the component should update, or false otherwise
   */
  static shouldComponentUpdate({
    context,
    nextProps,
    nextState,
    deepCompareProps,
    deepCompareStates,
  }) {
    const areObjectsEqual =
      CommonUtil.areObjectsEqual(context.props, nextProps) &&
      CommonUtil.areObjectsEqual(context.state, nextState);

    if (areObjectsEqual) {
      const arePropsEquivalent = deepCompareReactProperties(
        'property',
        deepCompareProps,
        context.props || {},
        nextProps,
      );
      if (!arePropsEquivalent) {
        return true; // If they're not equivalent, we should update
      }

      const areStatesEquivalent = deepCompareReactProperties(
        'property',
        deepCompareStates,
        context.state || {},
        nextState,
      );
      if (!areStatesEquivalent) {
        return true; // If they're not equivalent, we should update
      }
    }
    return !areObjectsEqual;
  }

  /**
   * Helper to capture accessible keyboard clicks
   * @callback       callback Actions the keydown event, if it matches `keys`
   * @param  {Array} keys     Contains the key codes to match against (DEFAULT: Enter)
   * @return {Func}           The function that will check for key code matches
   *                          and fire off the callback, if need be.
   */
  static a11yKey(callback, keys = [13]) {
    return (event) => {
      if (isFunction(callback) && keys.includes(event.keyCode)) {
        event.preventDefault();
        event.stopPropagation();
        callback(event);
      }
    };
  }

  /**
   * Determines whether a component should update
   * @param  {Object} fields          List of fields in the form (fieldProp: fieldName)
   * @param  {String} fieldToFocusOn  The name of the field we'd like to focus on
   * @return {String}                 Returns the actual name of a matched field to focus on
   */
  static getFieldToFocusOn({ fields, fieldToFocusOn }) {
    for (const fieldProp of Object.keys(fields)) {
      const fieldValue = fields[fieldProp];
      if (isObject(fieldValue)) {
        const nestedValue = this.getFieldToFocusOn({
          fields: fieldValue,
          fieldToFocusOn,
        });
        if (isString(nestedValue)) {
          return nestedValue;
        }
      } else if (fieldValue.includes(fieldToFocusOn) || fieldProp.includes(fieldToFocusOn)) {
        return fieldValue;
      }
    }
    return null;
  }
}

export default ComponentUtil;
export const nodeHasNewProps = ComponentUtil.nodeHasNewProps;
export const shouldComponentUpdate = ComponentUtil.shouldComponentUpdate;
export const a11yKey = ComponentUtil.a11yKey;
export const getFieldToFocusOn = ComponentUtil.getFieldToFocusOn;
export const getElementToFocusOn = ComponentUtil.getElementToFocusOn;
